import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Context
import { useAuth } from './context/AuthContext';
import { getDefaultRoute } from './utils/getDefaultRoute';

// Theme
import theme from './theme/theme';

// Components
import Layout from './components/Layout/Layout';
import PrivateRoute from './components/PrivateRoute';
import SupplierProfileGuard from './components/SupplierProfileGuard';
import InternalPasswordGuard from './components/InternalPasswordGuard';
import MaintenanceGuard from './components/MaintenanceGuard';

// Pages
import LandingPage from './pages/Landing/LandingPage';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import TOTPSetup from './pages/Auth/TOTPSetup';
import TOTPVerify from './pages/Auth/TOTPVerify';
import EmailCode from './pages/Auth/EmailCode';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import Dashboard from './pages/Dashboard/Dashboard';
import SupplierApplication from './pages/Supplier/SupplierApplication';
import ApplicationStatus from './pages/Supplier/ApplicationStatus';
import SupplierList from './pages/Admin/SupplierList';
import SupplierDetails from './pages/Admin/SupplierDetails';
import TaskListWrapper from './pages/Admin/TaskListWrapper';
import AllTasks from './pages/Admin/AllTasks';
import ContractList from './pages/Contracts/ContractList';
import ContractDetails from './pages/Contracts/ContractDetails';
import UserManagement from './pages/Admin/UserManagement';
import Reports from './pages/Reports/Reports';
import Profile from './pages/Profile/Profile';
import EditContactInfo from './pages/Profile/EditContactInfo';
import EditCompanyDetails from './pages/Profile/EditCompanyDetails';
import ChangePassword from './pages/Profile/ChangePassword';
import NotFound from './pages/NotFound';
import AuditLogs from './pages/Admin/AuditLogs';
import RegistrationApprovals from './pages/Admin/RegistrationApprovals';
import RegistrationDetails from './pages/Admin/RegistrationDetails';
import Setups from './pages/Admin/Setups';

function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      <Routes>
        {/* Landing Page */}
        <Route
          path="/"
          element={!user ? <LandingPage /> : <SupplierProfileGuard><Navigate to={getDefaultRoute(user)} /></SupplierProfileGuard>}
        />

        {/* Public Routes */}
        <Route
          path="/login"
          element={!user ? <Login /> : <SupplierProfileGuard><Navigate to={getDefaultRoute(user)} /></SupplierProfileGuard>}
        />
        <Route
          path="/register"
          element={!user ? <Register /> : <SupplierProfileGuard><Navigate to={getDefaultRoute(user)} /></SupplierProfileGuard>}
        />
        <Route
          path="/totp-setup"
          element={!user ? <TOTPSetup /> : <Navigate to={getDefaultRoute(user)} replace />}
        />
        <Route
          path="/email-code"
          element={!user ? <EmailCode /> : <Navigate to={getDefaultRoute(user)} replace />}
        />
        <Route
          path="/totp-verify"
          element={!user ? <TOTPVerify /> : <Navigate to={getDefaultRoute(user)} replace />}
        />
        <Route
          path="/forgot-password"
          element={!user ? <ForgotPassword /> : <Navigate to={getDefaultRoute(user)} />}
        />
        <Route
          path="/reset-password/:token"
          element={!user ? <ResetPassword /> : <Navigate to={getDefaultRoute(user)} />}
        />

        {/* Protected Routes */}
        <Route element={<PrivateRoute><InternalPasswordGuard><MaintenanceGuard><Layout /></MaintenanceGuard></InternalPasswordGuard></PrivateRoute>}>
          <Route path="/dashboard" element={<SupplierProfileGuard><Dashboard /></SupplierProfileGuard>} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/profile/edit-contact" element={<EditContactInfo />} />
          <Route path="/profile/edit-company" element={<EditCompanyDetails />} />
          <Route path="/change-password" element={<ChangePassword />} />

          {/* Supplier Routes */}
          <Route path="/application/new" element={<SupplierApplication />} />
          <Route path="/application/:id/edit" element={<SupplierApplication />} />
          <Route path="/application/:id" element={<ApplicationStatus />} />

          {/* Admin/Procurement/Legal Routes */}
          <Route path="/registrations" element={<RegistrationApprovals />} />
          <Route path="/registrations/:id" element={<RegistrationDetails />} />
          <Route path="/suppliers" element={<SupplierList />} />
          <Route path="/suppliers/:id" element={<SupplierDetails />} />
          <Route path="/tasks" element={<TaskListWrapper />} />
          <Route path="/tasks/all" element={<AllTasks />} />

          {/* Contract Routes */}
          <Route path="/contracts" element={<ContractList />} />
          <Route path="/contracts/:id" element={<ContractDetails />} />

          {/* User Management */}
          <Route path="/users" element={<UserManagement />} />

          {/* Reports */}
          <Route path="/reports" element={<Reports />} />

          {/* Audit Logs */}
          <Route path="/audit-logs" element={<AuditLogs />} />

          {/* Setups */}
          <Route path="/setups" element={<Setups />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </ThemeProvider>
  );
}

export default App;
